import {
  Table,
  Checkbox,
  Group,
  Text,
  ActionIcon,
  Select,
  Pagination,
  Menu,
  Box,
  Loader,
  useMantineTheme,
  MantineSize,
  Stack,
} from '@mantine/core';
import {
  IconSearch,
  IconFilter,
  IconSortAscending,
  IconSortDescending,
  IconDotsVertical,
  IconDownload,
  IconRefresh,
} from '@tabler/icons-react';
import { useState, useMemo, forwardRef, useEffect, useRef } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { Badge, Paper } from '@/components/ui';
import { SearchInput } from '@/components/ui/inputs';
import { AccessRule, AccessControl } from '@/components/security/AccessControl';
import { usePreferencesStore } from '../../../stores';

export interface DataTableColumn<T> {
  key: keyof T | string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  width?: number | string;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
}

export interface DataTableProps<T extends { id: string | number }> {
  data: T[];
  columns: DataTableColumn<T>[];
  loading?: boolean;
  selectable?: boolean;
  selectedRows?: (string | number)[];
  onSelectionChange?: (ids: (string | number)[]) => void;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
  };
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  onSearch?: (query: string) => void;
  searchValue?: string;
  onFilter?: (filters: Record<string, any>) => void;
  onRowClick?: (row: T) => void;
  onExport?: () => void;
  onRefresh?: () => void;
  emptyMessage?: string;
  searchPlaceholder?: string;
  actions?: (row: T) => React.ReactNode;
  bulkActions?: React.ReactNode;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  striped?: boolean;
  highlightOnHover?: boolean;
  stickyHeader?: boolean;
  responsiveBreakpoint?: MantineSize | number;
  enableColumnVisibility?: boolean;
  defaultVisibleColumns?: string[];
  onVisibleColumnsChange?: (columns: string[]) => void;
  enableInternalSearch?: boolean;
  enableInternalSort?: boolean;
  filters?: { key: string; label: string; options: { value: string; label: string }[] }[];
  withRowAnimations?: boolean;
  infiniteScroll?: {
    enabled: boolean;
    loadMore: () => void;
    hasMore: boolean;
    loading?: boolean;
    offset?: number;
  };
  rowActionAccessRule?: (row: T) => AccessRule | undefined;
  rowActionDeniedMessage?: string;
  localeFormatting?: boolean;
  viewStorageKey?: string;
  exportCsvFilename?: string;
}

function DataTableInner<T extends { id: string | number }>(
  {
    data,
    columns,
    loading = false,
    selectable = false,
    selectedRows = [],
    onSelectionChange,
    pagination,
    onSort,
    onSearch,
    searchValue,
    onRowClick,
    onExport,
    onRefresh,
    emptyMessage = 'No data available',
    searchPlaceholder = 'Search...',
    actions,
    bulkActions,
    gradientVariant = 'primary',
    striped = true,
    highlightOnHover = true,
    stickyHeader = false,
    responsiveBreakpoint = 'sm',
    enableColumnVisibility = true,
    defaultVisibleColumns,
    onVisibleColumnsChange,
    enableInternalSearch = true,
    enableInternalSort = true,
    filters,
    withRowAnimations = false,
    infiniteScroll,
    rowActionAccessRule,
    rowActionDeniedMessage = 'You do not have permission to perform this action',
    localeFormatting = true,
    viewStorageKey,
    exportCsvFilename,
  }: DataTableProps<T>,
  ref: React.Ref<HTMLDivElement>
) {
  const theme = useMantineTheme();
  const gradient = theme.other?.gradients?.[gradientVariant];
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    defaultVisibleColumns ?? columns.map((col) => String(col.key))
  );
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const locale = usePreferencesStore((state) => state.locale);
  const timezone = usePreferencesStore((state) => state.timezone);
  const uiSize = usePreferencesStore((state) => state.uiSize);
  const highContrast = usePreferencesStore((state) => state.highContrast);

  const allSelected = data.length > 0 && selectedRows.length === data.length;
  const someSelected = selectedRows.length > 0 && selectedRows.length < data.length;
  // Build a valid max-width media query. Theme breakpoints are em strings
  // (e.g. "48em"); numeric overrides are treated as px. (Previously this
  // appended "px" to "48em" → "48empx", an invalid query, so the mobile card
  // mode never activated.)
  const breakpointValue =
    typeof responsiveBreakpoint === 'number'
      ? `${responsiveBreakpoint}px`
      : theme.breakpoints?.[responsiveBreakpoint as MantineSize] || '48em';
  const isStacked = useMediaQuery(`(max-width: ${breakpointValue})`) ?? false;
  const densitySpacing = uiSize === 'compact' ? 'xs' : uiSize === 'comfortable' ? 'sm' : 'md';
  const borderColor = highContrast ? theme.colors.gray[5] : theme.colors.gray[3];

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange?.([]);
    } else {
      onSelectionChange?.(data.map((row) => row.id));
    }
  };

  const handleSelectRow = (id: string | number) => {
    if (selectedRows.includes(id)) {
      onSelectionChange?.(selectedRows.filter((rowId) => rowId !== id));
    } else {
      onSelectionChange?.([...selectedRows, id]);
    }
  };

  const handleSort = (key: string) => {
    const newDirection = sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortDirection(newDirection);
    onSort?.(key, newDirection);
  };

  const handleSearch = (query: string) => {
    // Only update local state if searchValue is not provided (uncontrolled mode)
    if (searchValue === undefined) {
      setSearchQuery(query);
    }
    onSearch?.(query);
  };

  const getValue = (row: T, key: string): any => {
    return key.split('.').reduce((obj: any, k) => obj?.[k], row);
  };

  const formatCellValue = (value: any) => {
    if (!localeFormatting) return value;
    if (value === null || value === undefined) return value;
    if (value instanceof Date || (typeof value === 'string' && !Number.isNaN(Date.parse?.(value)))) {
      try {
        return new Intl.DateTimeFormat(locale || 'en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: timezone,
        }).format(new Date(value));
      } catch {
        return value;
      }
    }
    if (typeof value === 'number') {
      try {
        return new Intl.NumberFormat(locale || 'en-US').format(value);
      } catch {
        return value;
      }
    }
    return value;
  };

  const filteredData = useMemo(() => {
    let rows = [...data];

    // Internal search if external handler not provided
    if (enableInternalSearch && !onSearch && searchQuery) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter((row) =>
        visibleColumns.some((colKey) => {
          const value = getValue(row, colKey);
          return value && String(value).toLowerCase().includes(q);
        })
      );
    }

    // Simple equals filters
    if (filters && Object.keys(activeFilters).length > 0) {
      rows = rows.filter((row) =>
        filters.every((filter) => {
          const selected = activeFilters[filter.key];
          if (!selected) return true;
          const value = getValue(row, filter.key);
          return String(value) === selected;
        })
      );
    }

    // Internal sort when external handler absent
    if (enableInternalSort && !onSort && sortKey) {
      rows = [...rows].sort((a, b) => {
        const aVal = getValue(a, sortKey);
        const bVal = getValue(b, sortKey);
        if (aVal === bVal) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return sortDirection === 'asc' ? -1 : 1;
      });
    }

    return rows;
  }, [data, enableInternalSearch, onSearch, searchQuery, visibleColumns, filters, activeFilters, enableInternalSort, onSort, sortKey, sortDirection]);

  // Client-side pagination: slice the filtered data (pageSize 0 = show all)
  const paginatedData = useMemo(() => {
    if (!pagination) return filteredData;
    if (pagination.pageSize <= 0) return filteredData;

    const startIndex = (pagination.page - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, pagination]);

  useEffect(() => {
    onVisibleColumnsChange?.(visibleColumns);
  }, [visibleColumns, onVisibleColumnsChange]);

  useEffect(() => {
    if (!viewStorageKey) return;
    const raw = localStorage.getItem(viewStorageKey);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as {
        visibleColumns?: string[];
        sortKey?: string | null;
        sortDirection?: 'asc' | 'desc';
        searchQuery?: string;
        filters?: Record<string, string>;
      };
      if (parsed.visibleColumns?.length) setVisibleColumns(parsed.visibleColumns);
      if (parsed.sortKey) setSortKey(parsed.sortKey);
      if (parsed.sortDirection) setSortDirection(parsed.sortDirection);
      if (parsed.searchQuery) setSearchQuery(parsed.searchQuery);
      if (parsed.filters) setActiveFilters(parsed.filters);
    } catch {
      // ignore parse errors
    }
  }, [viewStorageKey]);

  useEffect(() => {
    if (!viewStorageKey) return;
    const payload = JSON.stringify({
      visibleColumns,
      sortKey,
      sortDirection,
      searchQuery,
      filters: activeFilters,
    });
    localStorage.setItem(viewStorageKey, payload);
  }, [viewStorageKey, visibleColumns, sortKey, sortDirection, searchQuery, activeFilters]);

  useEffect(() => {
    if (!infiniteScroll?.enabled) return;
    const offset = infiniteScroll.offset ?? 0.3;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && infiniteScroll.hasMore && !infiniteScroll.loading) {
          infiniteScroll.loadMore();
        }
      },
      {
        threshold: offset,
      }
    );

    const node = loadMoreRef.current;
    if (node) observer.observe(node);
    return () => observer.disconnect();
  }, [infiniteScroll?.enabled, infiniteScroll?.hasMore, infiniteScroll?.loading, infiniteScroll?.loadMore, infiniteScroll?.offset]);

  const columnsToRender = enableColumnVisibility
    ? columns.filter((col) => visibleColumns.includes(String(col.key)))
    : columns;

  const renderValue = (row: T, column: DataTableColumn<T>, rowIndex: number) => {
    const raw = getValue(row, String(column.key));
    return column.render ? column.render(raw, row, rowIndex) : formatCellValue(raw);
  };

  const renderFilters = filters && filters.length > 0;

  const handleExportCsv = () => {
    const header = columnsToRender.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(',');
    const rows = filteredData.map((row, rowIndex) =>
      columnsToRender
        .map((col) => {
          const val = renderValue(row, col, rowIndex);
          const text = typeof val === 'string' ? val : typeof val === 'number' ? val.toString() : (val as any)?.props?.children ?? '';
          return `"${String(text).replace(/"/g, '""')}"`;
        })
        .join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = exportCsvFilename || 'table-export.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box ref={ref} p={0}>
      {/* Toolbar */}
      <Box p="md">
        <Group justify="space-between">
          <Group>
            {onSearch && (
              <SearchInput
                placeholder={searchPlaceholder}
                value={searchValue ?? searchQuery}
                onChange={(value) => handleSearch(value)}
                style={{ width: 250 }}
                gradientVariant={gradientVariant}
              />
            )}
            {!onSearch && enableInternalSearch && (
              <SearchInput
                placeholder={searchPlaceholder}
                value={searchValue ?? searchQuery}
                onChange={(value) => handleSearch(value)}
                style={{ width: 250 }}
                gradientVariant={gradientVariant}
              />
            )}
            {renderFilters && (
              <Group gap="xs">
                {filters?.map((filter) => (
                  <Select
                    key={filter.key}
                    placeholder={filter.label}
                    leftSection={<IconFilter size={14} />}
                    data={filter.options}
                    value={activeFilters[filter.key] || null}
                    onChange={(value) => setActiveFilters((prev) => ({ ...prev, [filter.key]: value || '' }))}
                    clearable
                    size="xs"
                  />
                ))}
              </Group>
            )}
            {selectedRows.length > 0 && (
              <Badge variant="light" color="blue">
                {selectedRows.length} selected
              </Badge>
            )}
            {selectedRows.length > 0 && bulkActions}
          </Group>
          <Group>
            {onRefresh && (
              <ActionIcon variant="subtle" onClick={onRefresh} loading={loading}>
                <IconRefresh size={18} />
              </ActionIcon>
            )}
            {(onExport || exportCsvFilename) && (
              <ActionIcon variant="subtle" onClick={onExport || handleExportCsv}>
                <IconDownload size={18} />
              </ActionIcon>
            )}
          </Group>
        </Group>
      </Box>

      {/* Responsive cards for small screens */}
      {isStacked ? (
        <StackedCards
          data={paginatedData}
          columns={columnsToRender}
          selectable={selectable}
          selectedRows={selectedRows}
          onSelectRow={handleSelectRow}
          onRowClick={onRowClick}
          actions={actions}
          gradientFrom={gradient?.from}
          withRowAnimations={withRowAnimations}
          renderValue={renderValue}
          densitySpacing={densitySpacing as MantineSize}
        />
      ) : (
        <Table.ScrollContainer minWidth={800}>
          <Table
            striped={striped}
            highlightOnHover={highlightOnHover}
            stickyHeader={stickyHeader}
            verticalSpacing={densitySpacing as any}
          >
            <Table.Thead>
              <Table.Tr>
                {selectable && (
                  <Table.Th style={{ width: 40 }}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleSelectAll}
                    />
                  </Table.Th>
                )}
                {columnsToRender.map((column) => (
                  <Table.Th
                    key={String(column.key)}
                    style={{
                      width: column.width,
                      cursor: column.sortable ? 'pointer' : 'default',
                      textAlign: column.align,
                    }}
                    onClick={() => column.sortable && handleSort(String(column.key))}
                  >
                    <Group gap={4} justify={column.align === 'right' ? 'flex-end' : 'flex-start'}>
                      <Text fw={600} size="sm">
                        {column.label}
                      </Text>
                      {column.sortable && sortKey === column.key && (
                        sortDirection === 'asc' ? (
                          <IconSortAscending size={14} />
                        ) : (
                          <IconSortDescending size={14} />
                        )
                      )}
                    </Group>
                  </Table.Th>
                ))}
                {actions && <Table.Th style={{ width: 60 }} />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={columnsToRender.length + (selectable ? 1 : 0) + (actions ? 1 : 0)}>
                    <Group justify="center" py="xl">
                      <Loader size="sm" />
                      <Text size="sm" c="dimmed">Loading...</Text>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ) : filteredData.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={columnsToRender.length + (selectable ? 1 : 0) + (actions ? 1 : 0)}>
                    <Text ta="center" py="xl" c="dimmed">
                      {emptyMessage}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                paginatedData.map((row, rowIndex) => (
                  <Table.Tr
                    key={row.id}
                    onClick={() => onRowClick?.(row)}
                    style={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      animation: withRowAnimations ? 'fadeInUp 220ms ease' : undefined,
                      animationDelay: withRowAnimations ? `${rowIndex * 20}ms` : undefined,
                    }}
                    bg={selectedRows.includes(row.id) ? `${gradient?.from}10` : undefined}
                  >
                    {selectable && (
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedRows.includes(row.id)}
                          onChange={() => handleSelectRow(row.id)}
                        />
                      </Table.Td>
                    )}
                    {columnsToRender.map((column) => (
                      <Table.Td key={String(column.key)} style={{ textAlign: column.align }}>
                        {renderValue(row, column, rowIndex)}
                      </Table.Td>
                    ))}
                    {actions && (
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Menu shadow="md" width={150} position="bottom-end">
                          <Menu.Target>
                            <ActionIcon variant="subtle">
                              <IconDotsVertical size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            {rowActionAccessRule ? (
                              <AccessControl
                                mode="disable"
                                disabledMessage={rowActionDeniedMessage}
                                {...rowActionAccessRule(row)}
                              >
                                {actions(row)}
                              </AccessControl>
                            ) : (
                              actions(row)
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {infiniteScroll?.enabled && (
        <Box ref={loadMoreRef} py="sm">
          {infiniteScroll.loading && (
            <Group justify="center">
              <Loader size="xs" />
              <Text size="xs" c="dimmed">
                Loading more...
              </Text>
            </Group>
          )}
        </Box>
      )}

      {/* Pagination */}
      {pagination && (
        <Box p="md">
          <Group justify="space-between">
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                Rows per page:
              </Text>
              <Select
                size="xs"
                w={90}
                value={pagination.pageSize <= 0 ? '0' : String(pagination.pageSize)}
                onChange={(value) => {
                  const newSize = Number(value);
                  pagination.onPageSizeChange?.(newSize);
                  if (newSize > 0) pagination.onPageChange(1);
                }}
                data={[
                  { value: '10', label: '10' },
                  { value: '25', label: '25' },
                  { value: '50', label: '50' },
                  { value: '100', label: '100' },
                  { value: '0', label: 'All' },
                ]}
              />
            </Group>
            {pagination.pageSize > 0 && (
              <Pagination
                total={Math.ceil(filteredData.length / pagination.pageSize)}
                value={pagination.page}
                onChange={pagination.onPageChange}
                size="sm"
              />
            )}
          </Group>
        </Box>
      )}

      {withRowAnimations && (
        <style>
          {`
            @keyframes fadeInUp {
              from { opacity: 0; transform: translateY(6px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}
        </style>
      )}
    </Box>
  );
}

interface StackedCardsProps<T extends { id: string | number }> {
  data: T[];
  columns: DataTableColumn<T>[];
  selectable?: boolean;
  selectedRows: (string | number)[];
  onSelectRow: (id: string | number) => void;
  onRowClick?: (row: T) => void;
  actions?: (row: T) => React.ReactNode;
  gradientFrom?: string;
  withRowAnimations?: boolean;
  renderValue: (row: T, column: DataTableColumn<T>, rowIndex: number) => React.ReactNode;
  densitySpacing: MantineSize;
}

function StackedCards<T extends { id: string | number }>({
  data,
  columns,
  selectable,
  selectedRows,
  onSelectRow,
  onRowClick,
  actions,
  gradientFrom,
  withRowAnimations,
  renderValue,
  densitySpacing,
}: StackedCardsProps<T>) {
  return (
    <Stack gap="sm" p="sm">
      {data.length === 0 ? (
        <Text ta="center" c="dimmed">
          No data available
        </Text>
      ) : (
        data.map((row, index) => (
          <Paper
            key={row.id}
            withBorder
            p={densitySpacing}
            style={{
              cursor: onRowClick ? 'pointer' : 'default',
              backgroundColor: selectedRows.includes(row.id) ? `${gradientFrom}10` : undefined,
              animation: withRowAnimations ? 'fadeInUp 220ms ease' : undefined,
              animationDelay: withRowAnimations ? `${index * 20}ms` : undefined,
            }}
            onClick={() => onRowClick?.(row)}
          >
            <Group justify="space-between" align="flex-start" mb="xs">
              {selectable && (
                <Checkbox
                  checked={selectedRows.includes(row.id)}
                  onChange={() => onSelectRow(row.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {actions && (
                <Menu shadow="md" width={150} position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="subtle">
                      <IconDotsVertical size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>{actions(row)}</Menu.Dropdown>
                </Menu>
              )}
            </Group>
            <Stack gap={4}>
              {columns.map((col) => (
                <Group key={String(col.key)} justify="space-between" align="flex-start">
                  <Text size="xs" c="dimmed" fw={600}>
                    {col.label}
                  </Text>
                  <Text size="sm" ta="right">
                    {renderValue(row, col, index)}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Paper>
        ))
      )}
    </Stack>
  );
}

const DataTable = forwardRef(DataTableInner) as <T extends { id: string | number }>(
  props: DataTableProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;

export default DataTable;
